/**
 * DataTable primitives + KenDataTable engine — Ken's shared table primitive.
 *
 * Per ADR-030 + ADR-031 + the lo-fi entity list wireframe
 * (docs/ui/wireframes/05-entity-list.html).
 *
 * Two surfaces live in this file:
 *
 * 1. Low-level primitives (DataTable, DataTableHeader, DataTableHead,
 *    DataTableBody, DataTableRow, DataTableCell, DataTableEmpty) — the
 *    original headless `<table>` shell. Preserved verbatim as a low-level
 *    escape hatch for cases where the manifest engine is overkill.
 *
 * 2. KenDataTable — the new manifest-config-driven, headless engine that
 *    every future module list should use. Renders columns, sorting,
 *    filters, search, pagination (offset or cursor), row selection, row
 *    actions, bulk actions, confirm dialogs, loading + empty + error
 *    states, and a stacked-card responsive layout below 640px (or the
 *    host's `cardBreakpointPx`).
 *
 * State ownership: the host page owns sort/filter/page/search/selection.
 * KenDataTable is a controlled component — every interactive state is
 * (value, onChange) so the host can sync URL/query-string without the
 * primitive having an opinion. The only state KenDataTable owns
 * internally is ephemeral UI state (which confirm dialog is open, etc.).
 *
 * Engine note: this implementation uses a small in-file headless engine
 * (column derivation, sort-cycle UX, visibility) rather than
 * @tanstack/react-table. The package contract — manifest in, controlled
 * (value, onChange) out — is identical to a TanStack-backed
 * implementation, so swapping the internals later is a contained change.
 * Pagination/filtering/selection logic lives on the host already; the
 * engine's only job is column model + sort cycle.
 *
 * Auth note: @ken/ui must never depend on @ken/auth (the auth store
 * pulls in Zustand + the API client + the router; loading it into every
 * primitive would tank cockpit bundle size). The host page passes
 * `usePermission?: (slug) => boolean` so KenDataTable can hide
 * permission-gated row + bulk actions without knowing what backs the
 * permission system.
 */

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, Copy as DuplicateIcon, DollarSign, Ellipsis, Eye, Pencil, Sparkles, Trash, X } from "lucide-react";
import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type ComponentType,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";

import { formatDateOnly } from "../lib/date-only";
import { cn } from "../lib/utils";
import { StateDenied } from "./async-state";

import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Skeleton } from "./skeleton";

// ===========================================================================
// Low-level primitives — preserved for back-compat with the existing
// customer-list.tsx and any module that wants the raw table shell.
// ===========================================================================

export const DataTable = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-glass-border)] bg-white overflow-hidden">
      <table
        ref={ref}
        className={cn("w-full text-sm border-collapse", className)}
        {...props}
      />
    </div>
  ),
);
DataTable.displayName = "DataTable";

export const DataTableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("bg-[oklch(97%_0.005_240)]", className)}
    {...props}
  />
));
DataTableHeader.displayName = "DataTableHeader";

export const DataTableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("", className)} {...props} />
));
DataTableBody.displayName = "DataTableBody";

export const DataTableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-[oklch(0%_0_0_/_0.06)] last:border-b-0",
        "hover:bg-[oklch(98%_0.005_240)] transition-colors",
        className,
      )}
      {...props}
    />
  ),
);
DataTableRow.displayName = "DataTableRow";

export const DataTableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      scope="col"
      className={cn(
        "text-left px-3 py-2.5 text-[12px] uppercase tracking-wide font-semibold",
        "text-[var(--color-text-soft)] border-b-[1.4px] border-[oklch(0%_0_0_/_0.1)]",
        className,
      )}
      {...props}
    />
  ),
);
DataTableHead.displayName = "DataTableHead";

export const DataTableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn("px-3 py-3 align-middle", className)} {...props} />
  ),
);
DataTableCell.displayName = "DataTableCell";

export const DataTableEmpty = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="status"
      className={cn(
        "grid place-items-center min-h-40 p-6 text-center",
        "text-sm text-[var(--color-text-soft)]",
        className,
      )}
      {...props}
    />
  ),
);
DataTableEmpty.displayName = "DataTableEmpty";

// ===========================================================================
// Manifest types — public surface
// ===========================================================================

export type ColumnFormat = "currency" | "date" | "datetime" | "boolean" | "json";

export type ColumnAlign = "left" | "right" | "center";

export type ColumnFilterSpec = {
  type: "text" | "enum" | "number_range" | "date_range";
  options?: readonly string[];
};

export type ColumnSpec = {
  id: string;
  /** Bare label (fallback when no `label_term` is set). */
  label?: string;
  /** @ken/terminology key — preferred. Routed through `useTerm` by host. */
  label_term?: string;
  /** Path into the row, e.g. "core.name", "custom.delivery_route". Dotted lookup. */
  source: string;
  sortable?: boolean;
  filterable?: ColumnFilterSpec;
  default_visible?: boolean;
  pinned?: "left" | "right";
  width?: number;
  /** Lower = keep longest. priority 3 hides first under narrow widths. */
  priority?: 1 | 2 | 3;
  align?: ColumnAlign;
  format?: ColumnFormat;
  /**
   * Optional custom cell renderer. When set, takes precedence over
   * `format` — the renderer receives the full row, not just the
   * `source` value, so it can compose multi-field cells (e.g., the
   * Customer cell with avatar + name + sub-line). The row argument is
   * typed loosely so module-side manifests don't need to thread a
   * generic through every column declaration.
   */
  render?: (row: Record<string, unknown>) => ReactNode;
  /** Wave 2 — accepted but NO-OP. Reserved for inline editing. */
  editable?: { type: string };
};

export type RowActionIcon =
  | "eye"
  | "pencil"
  | "trash"
  | "duplicate"
  | "ellipsis"
  | "sparkles"
  | "dollar";

export type ConfirmSpec = {
  title?: string;
  body?: string;
  destructive?: boolean;
};

export type RowActionSpec = {
  id: string;
  label?: string;
  label_term?: string;
  icon?: RowActionIcon;
  /** Navigation target. `{id}` interpolates with the row's id. */
  to?: string;
  /** Server mutation hint, e.g. "DELETE /api/modules/customers/{id}". */
  mutation?: string;
  confirm?: ConfirmSpec;
  requires_permissions?: readonly string[];
  /** Per-row visibility predicate. Return true to hide this action for the
   *  given row (e.g. "Record payment" on an already-paid invoice). Runs in
   *  addition to the permission filter. */
  hidden?: (row: Record<string, unknown>) => boolean;
};

export type BulkActionSpec = {
  id: string;
  label?: string;
  label_term?: string;
  /** Server endpoint hint, e.g. "POST /api/modules/customers/bulk-archive". */
  endpoint?: string;
  confirm?: { title?: string; body?: string };
  requires_permissions?: readonly string[];
};

export type FilterSpec = {
  id: string;
  label?: string;
  label_term?: string;
  source: string;
  type: "enum";
  options: readonly string[];
  /**
   * Optional per-option record counts for chip_group filters.
   * Key matches an option value (or "all" for the All chip).
   * When present, each chip renders as "Label (N)"; when absent the chip
   * renders the label only.  Graceful: missing keys render without a count.
   */
  option_counts?: Record<string, number> | undefined;
  default?: string;
  display: "chip_group" | "dropdown";
};

export type TableManifest = {
  /** For testid prefix per ADR-031. */
  moduleId: string;
  /** For testid prefix per ADR-031. */
  entityId: string;
  columns: readonly ColumnSpec[];
  filters?: readonly FilterSpec[];
  row_actions?: readonly RowActionSpec[];
  bulk_actions?: readonly BulkActionSpec[];
  default_sort?: { column: string; direction: "asc" | "desc" };
  page_size?: number;
  row_selection?: "none" | "single" | "multi";
};

// ===========================================================================
// Controlled-state types
// ===========================================================================

export type SortState = { column: string; direction: "asc" | "desc" } | null;

export type FilterState = Record<string, string | undefined>;

export type RowData = Record<string, unknown> & { id: string | number };

// ===========================================================================
// Host-supplied renderer/hook props — keeps the package agnostic
// ===========================================================================

/**
 * Optional renderer for navigation links. Modules wire this to TanStack
 * Router (or react-router) so KenDataTable never imports a router itself.
 * If absent, falls back to a plain `<a href>`.
 */
export type LinkRenderer = ComponentType<
  AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }
>;

/**
 * Optional permission check. Host wires this to @ken/auth's
 * `usePermission` — but the prop signature is a plain function so any
 * back-end works.
 */
export type PermissionCheck = (slug: string) => boolean;

/**
 * Per-row presentation hint — the "three registers" machinery.
 *
 * A host that renders more than one KIND of row in a single table (a tree:
 * group headers, entity rows, terminal leaves) returns a descriptor per row.
 * KenDataTable does two things with it and nothing more:
 *
 *   • stamps `kind` on the `<tr>` as `data-row-kind`, so the register's
 *     visual treatment lives in CSS (`tr[data-row-kind=…]` in tokens.css)
 *     rather than in per-cell Tailwind — themeable, and the table primitive
 *     stays ignorant of any module's vocabulary;
 *   • drops the chrome that is meaningless on that row — the selection
 *     checkbox and/or the ⋯ action menu. The `<td>`s stay (EMPTY), so the
 *     column count, `aria-colcount` and the column grid are untouched.
 *
 * Rows flagged `hideSelection` are excluded from the header select-all: they
 * carry synthetic ids that no bulk endpoint can act on, so including them
 * both poisoned the selected-id set and left the "all selected" checkbox
 * permanently unchecked.
 *
 * Entirely optional — omit `rowPresentation` and every row renders exactly
 * as before.
 */
export type RowPresentation = {
  /** Register name, stamped verbatim as `data-row-kind` on the `<tr>`. */
  kind?: string;
  /** Render an empty selection cell (no checkbox) + exclude from select-all. */
  hideSelection?: boolean;
  /** Render an empty actions cell (no ⋯ menu). */
  hideActions?: boolean;
};

// ===========================================================================
// KenDataTable props
// ===========================================================================

export interface KenDataTableProps<R extends RowData = RowData> {
  manifest: TableManifest;
  data: readonly R[];

  // ---- query state (all controlled) ----
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Debounce in ms for the search input. Default 300. */
  searchDebounceMs?: number;

  sorting?: SortState;
  onSortingChange?: (sorting: SortState) => void;

  filters?: FilterState;
  onFiltersChange?: (filters: FilterState) => void;

  // ---- pagination — offset OR cursor; pick one ----
  /** 1-indexed page number for offset pagination. */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  /** Opaque cursor for the next page — when supplied, cursor mode is used. */
  nextCursor?: string | null;
  onCursorNext?: () => void;
  /** Optional — when undefined, no "prev" button is shown in cursor mode. */
  onCursorPrev?: () => void;

  // ---- selection (controlled) ----
  selectedIds?: ReadonlySet<string | number>;
  onSelectedIdsChange?: (ids: ReadonlySet<string | number>) => void;

  // ---- callbacks ----
  /**
   * Fires when the user clicks anywhere on a row (outside the selection
   * checkbox + row-action menu). The standard pattern for opening a
   * detail sheet — host hooks this up to a `setSelectedCustomer(row)`
   * + open-sheet flow. When undefined, rows are not click-targetable
   * (legacy behaviour).
   */
  onRowClick?: (row: R) => void;
  onRowAction?: (action: RowActionSpec, row: R) => void;
  onBulkAction?: (
    action: BulkActionSpec,
    ids: ReadonlySet<string | number>,
  ) => void;

  // ---- lifecycle ----
  loading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  /**
   * UI-20. The list read was REFUSED, not broken.
   *
   * A 403 used to arrive here as `error`, so a closed door rendered the red
   * "Something went wrong" panel with a Retry button that could only fail
   * again. The two are different answers and only one of them is worth acting
   * on. `denied` renders `role="status"`, an explanation, and no Retry.
   */
  denied?: boolean;

  // ---- presentation ----
  /**
   * Resolver for `label_term` keys. Host wires this to @ken/terminology
   * `useTerm`. If absent, falls back to the column's `label` or `id`.
   */
  resolveTerm?: (termKey: string) => string;

  /** Optional link renderer (TanStack Router Link, react-router Link, etc.). */
  LinkComponent?: LinkRenderer;

  /** Permission gate — see `PermissionCheck`. Defaults to permit-all. */
  usePermission?: PermissionCheck;

  /**
   * Per-row register descriptor — see `RowPresentation`. Additive: when
   * omitted every row renders with the default chrome.
   */
  rowPresentation?: (row: R) => RowPresentation | undefined;

  /** Add-entity CTA target. When set + no-data + no-filters, renders an Add button. */
  addEntityTo?: string;
  /** Add-entity CTA label (already resolved by host). */
  addEntityLabel?: string;

  /** Override className on the outer container. */
  className?: string;

  /**
   * The table's `<caption>`: its accessible name, and the place to say how it
   * is sorted ("Conversations, sorted by last text, newest first"). A plain
   * table (no `grid` role) is read by its caption and headers, so a host
   * whose sort matters to the reader says it here. Rendered visually hidden.
   */
  caption?: ReactNode;

  /**
   * Below this width (px) rows render as stacked cards instead of a table.
   * Default 640. A host inside a layout that already switches at another
   * width (the inbox goes single-panel below 768 px) passes that width so
   * the two layouts never disagree.
   */
  cardBreakpointPx?: number;
}

// ===========================================================================
// Engine — minimal headless: column model + sort cycle + dotted lookup.
// ===========================================================================

/**
 * Look up `dotted.path` against a row. Returns `undefined` on miss.
 */
function lookupSource(row: RowData, source: string): unknown {
  if (!source) return undefined;
  const parts = source.split(".");
  let current: unknown = row;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Accessible name for a row's selection checkbox.
 *
 * Previously `Select row ${row.id}`, which reads a UUID aloud — a screen
 * reader user selecting from a list heard "Select row 8f3a-4c2b-…" fifty
 * times over with nothing to tell the rows apart. The first column is the
 * identifying one in every Ken list (name / number / title), so use its
 * value and fall back to the id only when there is genuinely nothing else.
 */
function selectionLabel(row: RowData, columns: readonly ColumnSpec[]): string {
  const first = columns[0];
  if (first) {
    const raw = lookupSource(row, first.source);
    if (raw != null && raw !== "") {
      const text = String(raw).trim();
      if (text) return `Select ${text}`;
    }
  }
  return `Select row ${row.id}`;
}

/**
 * Cycle a sort state for a column: asc → desc → none → asc.
 */
function cycleSort(current: SortState, columnId: string): SortState {
  if (!current || current.column !== columnId) {
    return { column: columnId, direction: "asc" };
  }
  if (current.direction === "asc") return { column: columnId, direction: "desc" };
  return null;
}

/**
 * Built-in cell formatters. Custom rendering is the host's job — pass a
 * pre-derived value via a `derived.*` source if you need anything fancier.
 */
function formatCell(value: unknown, format: ColumnFormat | undefined): string {
  if (value == null || value === "") return "";
  switch (format) {
    case "currency": {
      // Built-in currency formatting is deliberately removed.
      // Currency is a tenant concern, not a column-spec concern — callers
      // must provide a `render` function that uses <CurrencyValue currency={...}>
      // sourced from `useCurrency()`. Falling back to raw number stringify
      // is intentional so a stray `format: "currency"` column is visually
      // obvious as misconfigured.
      const n = typeof value === "number" ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    case "date": {
      // Date-ONLY columns (best_before_date, due_date, invoice_date…). A
      // strict YYYY-MM-DD value parses to LOCAL date parts so it renders
      // as the calendar day it names in every timezone — see date-only.ts
      // for the full "new Date(dateOnlyString) parses as UTC midnight" bug
      // writeup. Full ISO datetimes fall through formatDateOnly's own
      // normal-parse path, so this is a behavior no-op for the "datetime"
      // case below.
      return formatDateOnly(value);
    }
    case "datetime": {
      const d = value instanceof Date ? value : new Date(String(value));
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    }
    case "boolean":
      return value ? "Yes" : "No";
    case "json":
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    default:
      return String(value);
  }
}

// ===========================================================================
// Internal: label resolution
// ===========================================================================

function resolveLabel(
  spec: { label?: string; label_term?: string; id: string },
  resolveTerm: PermissionAndTerm["resolveTerm"],
): string {
  if (spec.label_term && resolveTerm) return resolveTerm(spec.label_term);
  if (spec.label) return spec.label;
  return spec.id;
}

type PermissionAndTerm = {
  resolveTerm?: (termKey: string) => string;
  usePermission: PermissionCheck;
};

function hasAllPermissions(
  required: readonly string[] | undefined,
  usePermission: PermissionCheck,
): boolean {
  if (!required || required.length === 0) return true;
  return required.every((slug) => usePermission(slug));
}

// ===========================================================================
// Icon resolver for row actions
// ===========================================================================

const ROW_ACTION_ICONS: Record<RowActionIcon, ComponentType<{ size?: number }>> = {
  eye: Eye,
  pencil: Pencil,
  trash: Trash,
  duplicate: DuplicateIcon,
  ellipsis: Ellipsis,
  sparkles: Sparkles,
  dollar: DollarSign,
};

// ===========================================================================
// Default link renderer — vanilla anchor.
// ===========================================================================

const DefaultLink: LinkRenderer = ({ to, children, ...rest }) => (
  <a href={to} {...rest}>
    {children}
  </a>
);

// ===========================================================================
// Viewport hook — drives desktop-vs-mobile layout via a single source.
//
// Using `useSyncExternalStore` so happy-dom (no matchMedia listener support)
// and SSR get the same "desktop" default. The store subscribes to a real
// `matchMedia` listener in browsers and resolves to `false` in tests where
// `window.matchMedia` is absent.
// ===========================================================================

const DEFAULT_CARD_BREAKPOINT_PX = 640;

function mobileQuery(breakpointPx: number): string {
  return `(max-width: ${breakpointPx - 1}px)`;
}

function getMobileServerSnapshot(): boolean {
  return false;
}

function useIsMobile(breakpointPx: number = DEFAULT_CARD_BREAKPOINT_PX): boolean {
  const query = mobileQuery(breakpointPx);
  const subscribe = useCallback(
    (callback: () => void): (() => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => {};
      }
      const mql = window.matchMedia(query);
      // Older Safari only supports addListener (deprecated but harmless).
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", callback);
        return () => mql.removeEventListener("change", callback);
      }
      mql.addListener(callback);
      return () => mql.removeListener(callback);
    },
    [query],
  );
  const getSnapshot = useCallback((): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, getMobileServerSnapshot);
}

// ===========================================================================
// Search debouncer hook
// ===========================================================================

function useDebouncedSearch(
  initial: string,
  onChange: ((value: string) => void) | undefined,
  delayMs: number,
): [string, (next: string) => void] {
  const [local, setLocal] = useState(initial);
  const lastEmitted = useRef(initial);

  // Stay in sync if the host overrides the value externally (e.g. URL change).
  useEffect(() => {
    if (initial !== lastEmitted.current) {
      setLocal(initial);
      lastEmitted.current = initial;
    }
  }, [initial]);

  useEffect(() => {
    if (!onChange) return;
    if (local === lastEmitted.current) return;
    const handle = setTimeout(() => {
      lastEmitted.current = local;
      onChange(local);
    }, delayMs);
    return () => clearTimeout(handle);
  }, [local, onChange, delayMs]);

  return [local, setLocal];
}

// ===========================================================================
// KenDataTable — the main component
// ===========================================================================

export function KenDataTable<R extends RowData = RowData>(
  props: KenDataTableProps<R>,
): ReactElement {
  const {
    manifest,
    data,
    search = "",
    onSearchChange,
    searchDebounceMs = 300,
    sorting = manifest.default_sort ?? null,
    onSortingChange,
    filters = {},
    onFiltersChange,
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    nextCursor,
    onCursorNext,
    onCursorPrev,
    selectedIds,
    onSelectedIdsChange,
    onRowAction,
    onRowClick,
    onBulkAction,
    loading = false,
    error = null,
    denied = false,
    onRetry,
    resolveTerm,
    LinkComponent = DefaultLink,
    usePermission = () => true,
    rowPresentation,
    addEntityTo,
    addEntityLabel,
    className,
    caption,
    cardBreakpointPx = DEFAULT_CARD_BREAKPOINT_PX,
  } = props;

  const { moduleId, entityId } = manifest;
  const testidPrefix = `${moduleId}-${entityId}`;
  const isMobile = useIsMobile(cardBreakpointPx);

  // Confirm-dialog state — ephemeral UI, not exposed.
  const [pendingConfirm, setPendingConfirm] = useState<
    | { kind: "row"; action: RowActionSpec; row: R }
    | { kind: "bulk"; action: BulkActionSpec }
    | null
  >(null);

  // ---- column model ----
  const visibleColumns = useMemo(
    () =>
      manifest.columns.filter(
        (c) => c.default_visible === undefined || c.default_visible === true,
      ),
    [manifest.columns],
  );

  // ---- selection ----
  const selection = selectedIds ?? new Set<string | number>();
  const selectionEnabled = manifest.row_selection && manifest.row_selection !== "none";
  const selectionMode = manifest.row_selection ?? "none";

  const toggleSelected = useCallback(
    (id: string | number, checked: boolean) => {
      if (!onSelectedIdsChange) return;
      const next = new Set(selection);
      if (selectionMode === "single") {
        next.clear();
        if (checked) next.add(id);
      } else {
        if (checked) next.add(id);
        else next.delete(id);
      }
      onSelectedIdsChange(next);
    },
    [selection, selectionMode, onSelectedIdsChange],
  );

  // Select-all operates on the SELECTABLE rows only. A tree host marks its
  // synthetic group / leaf rows `hideSelection` — those ids are not real
  // records, so sweeping them into the selection sent nonsense to bulk
  // endpoints and left the header checkbox stuck unchecked (it could never
  // be true for rows that render no checkbox).
  const selectableRows = useMemo(
    () =>
      rowPresentation
        ? data.filter((row) => rowPresentation(row)?.hideSelection !== true)
        : data,
    [data, rowPresentation],
  );

  const allOnPageSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selection.has(row.id));
  const someOnPageSelected =
    selectableRows.some((row) => selection.has(row.id)) && !allOnPageSelected;

  const toggleAllOnPage = useCallback(
    (checked: boolean) => {
      if (!onSelectedIdsChange) return;
      const next = new Set(selection);
      if (checked) {
        for (const row of selectableRows) next.add(row.id);
      } else {
        for (const row of selectableRows) next.delete(row.id);
      }
      onSelectedIdsChange(next);
    },
    [selectableRows, selection, onSelectedIdsChange],
  );

  // ---- sort ----
  const handleSort = useCallback(
    (columnId: string) => {
      if (!onSortingChange) return;
      onSortingChange(cycleSort(sorting, columnId));
    },
    [sorting, onSortingChange],
  );

  // ---- search ----
  const [localSearch, setLocalSearch] = useDebouncedSearch(
    search,
    onSearchChange,
    searchDebounceMs,
  );

  // ---- filters ----
  const setFilter = useCallback(
    (id: string, value: string | undefined) => {
      if (!onFiltersChange) return;
      const next: FilterState = { ...filters };
      if (value === undefined || value === "" || value === "__all__") {
        delete next[id];
      } else {
        next[id] = value;
      }
      onFiltersChange(next);
    },
    [filters, onFiltersChange],
  );

  const hasActiveFiltersOrSearch =
    localSearch.length > 0 ||
    Object.values(filters).some((v) => v !== undefined && v !== "");

  // ---- row count + each row's position ----
  // aria-rowcount counts every row of the table, the header row included, and
  // each row says where it stands (aria-rowindex), so "row 52 of 120" is true
  // on page 2. The count used to be the page's own length with no positions,
  // which told a screen-reader user a 50-row page was the whole list.
  // Unknown totals under offset paging read -1 (ARIA's "unknown"); cursor
  // paging has no stable position, so it states neither.
  const cursorPaged = nextCursor !== undefined || Boolean(onCursorNext);
  const offsetPaged = !cursorPaged && Boolean(onPageChange || onPageSizeChange);
  const ariaRowCount: number | undefined =
    loading || cursorPaged
      ? undefined
      : total !== undefined
        ? total + 1
        : offsetPaged
          ? -1
          : data.length + 1;
  const rowIndexBase = offsetPaged
    ? ((page ?? 1) - 1) * (pageSize ?? 25) // the same page size Pagination shows
    : 0;

  const clearFilters = useCallback(() => {
    setLocalSearch("");
    onSearchChange?.("");
    onFiltersChange?.({});
  }, [onFiltersChange, onSearchChange, setLocalSearch]);

  // ---- row-action click ----
  const handleRowActionClick = useCallback(
    (action: RowActionSpec, row: R) => {
      if (action.confirm) {
        setPendingConfirm({ kind: "row", action, row });
        return;
      }
      onRowAction?.(action, row);
    },
    [onRowAction],
  );

  const handleBulkActionClick = useCallback(
    (action: BulkActionSpec) => {
      if (action.confirm) {
        setPendingConfirm({ kind: "bulk", action });
        return;
      }
      onBulkAction?.(action, selection);
    },
    [onBulkAction, selection],
  );

  const confirmPending = useCallback(() => {
    if (!pendingConfirm) return;
    if (pendingConfirm.kind === "row") {
      onRowAction?.(pendingConfirm.action, pendingConfirm.row);
    } else {
      onBulkAction?.(pendingConfirm.action, selection);
    }
    setPendingConfirm(null);
  }, [onBulkAction, onRowAction, pendingConfirm, selection]);

  // ---- permission-denied state ----
  // Before `error`, because a call site that maps a 403 onto BOTH (the
  // straightforward thing to do when catching a response) should get the
  // refusal, which is the more specific and more useful of the two.
  if (denied) {
    return (
      <StateDenied
        idPrefix={testidPrefix}
        what="this list"
        className={className}
      />
    );
  }

  // ---- error state ----
  if (error) {
    return (
      <ErrorPanel
        message={typeof error === "string" ? error : error.message}
        onRetry={onRetry}
        testid={`${testidPrefix}-error`}
      />
    );
  }

  // ---- render ----
  return (
    <div
      className={cn("flex flex-col gap-4", className)}
      data-testid={`${testidPrefix}-table-shell`}
    >
      {/* Toolbar: search + filters */}
      {(onSearchChange ||
        (manifest.filters && manifest.filters.length > 0)) && (
        <Toolbar
          testidPrefix={testidPrefix}
          search={localSearch}
          onSearchChange={onSearchChange ? setLocalSearch : undefined}
          filters={filters}
          filterSpecs={manifest.filters}
          onFilterChange={setFilter}
          resolveTerm={resolveTerm}
        />
      )}

      {/* Bulk-action toolbar */}
      {selectionEnabled && selection.size > 0 && manifest.bulk_actions && (
        <BulkActionToolbar
          testidPrefix={testidPrefix}
          selectedCount={selection.size}
          bulkActions={manifest.bulk_actions}
          onAction={handleBulkActionClick}
          onClear={() => onSelectedIdsChange?.(new Set())}
          resolveTerm={resolveTerm}
          usePermission={usePermission}
        />
      )}

      {/* The actual table */}
      <div
        // overflow-x-auto, not overflow-hidden: columns are no longer dropped
        // by viewport (see tokens.css), so a narrow desktop window scrolls
        // sideways instead of silently losing the columns the user picked.
        className="rounded-[var(--radius-md)] border border-[var(--color-glass-border)] bg-white overflow-hidden overflow-x-auto"
        data-testid={`${testidPrefix}-table-wrap`}
      >
        {/* Desktop: <table>; Mobile (<640px): stacked cards. Driven by
         * matchMedia so happy-dom + SSR get the desktop layout (default).
         */}
        {/* A plain table, not role="grid": a grid promises arrow-key cell
         * movement this table does not have, so a screen-reader user was
         * told to use keys that did nothing. Tab reaches the links and
         * buttons; the caption and headers name the rest. */}
        {!isMobile && (
          <table
            {...(ariaRowCount !== undefined ? { "aria-rowcount": ariaRowCount } : {})}
            aria-colcount={
              visibleColumns.length +
              (selectionEnabled ? 1 : 0) +
              (manifest.row_actions && manifest.row_actions.length > 0 ? 1 : 0)
            }
            data-testid={`${testidPrefix}-table`}
            className="w-full text-sm border-collapse"
          >
            {caption ? (
              <caption
                className="sr-only"
                data-testid={`${testidPrefix}-table-caption`}
              >
                {caption}
              </caption>
            ) : null}
            <thead className="bg-[oklch(97%_0.005_240)]">
              <tr {...(ariaRowCount !== undefined ? { "aria-rowindex": 1 } : {})}>
                {selectionEnabled && (
                  <th
                    scope="col"
                    className="w-10 px-3 py-2.5 text-left text-[12px] uppercase tracking-wide font-semibold text-[var(--color-text-soft)] border-b-[1.4px] border-[oklch(0%_0_0_/_0.1)]"
                  >
                    {selectionMode === "multi" && (
                      <input
                        type="checkbox"
                        aria-label="Select all rows on this page"
                        checked={allOnPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someOnPageSelected;
                        }}
                        onChange={(e) => toggleAllOnPage(e.target.checked)}
                        data-testid={`${testidPrefix}-select-all`}
                        className="h-4 w-4 cursor-pointer"
                      />
                    )}
                  </th>
                )}
                {visibleColumns.map((col) => {
                  const label = resolveLabel(col, resolveTerm);
                  const isSorted = sorting?.column === col.id;
                  const ariaSort: "ascending" | "descending" | "none" = isSorted
                    ? sorting?.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none";
                  return (
                    <th
                      key={col.id}
                      scope="col"
                      aria-sort={col.sortable ? ariaSort : undefined}
                      data-testid={`${testidPrefix}-column-header-${col.id}`}
                      data-priority={col.priority ?? 1}
                      className={cn(
                        "px-3 py-2.5 text-[12px] uppercase tracking-wide font-semibold",
                        "text-[var(--color-text-soft)] border-b-[1.4px] border-[oklch(0%_0_0_/_0.1)]",
                        col.align === "right" && "text-right",
                        col.align === "center" && "text-center",
                        !col.align && "text-left",
                      )}
                      style={col.width ? { width: col.width } : undefined}
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.id)}
                          className="inline-flex items-center gap-1 hover:text-[var(--color-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] rounded-[var(--radius-sm)] px-1 -mx-1"
                        >
                          <span>{label}</span>
                          <SortIcon
                            direction={
                              isSorted ? sorting?.direction ?? null : null
                            }
                          />
                        </button>
                      ) : (
                        <span>{label}</span>
                      )}
                    </th>
                  );
                })}
                {manifest.row_actions && manifest.row_actions.length > 0 && (
                  <th
                    scope="col"
                    className="w-12 px-3 py-2.5 border-b-[1.4px] border-[oklch(0%_0_0_/_0.1)]"
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRows
                  columnCount={
                    visibleColumns.length +
                    (selectionEnabled ? 1 : 0) +
                    (manifest.row_actions && manifest.row_actions.length > 0 ? 1 : 0)
                  }
                  rowCount={pageSize ?? manifest.page_size ?? 10}
                  testidPrefix={testidPrefix}
                />
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      visibleColumns.length +
                      (selectionEnabled ? 1 : 0) +
                      (manifest.row_actions && manifest.row_actions.length > 0 ? 1 : 0)
                    }
                  >
                    <EmptyStateCell
                      hasFiltersOrSearch={hasActiveFiltersOrSearch}
                      onClearFilters={clearFilters}
                      addEntityTo={addEntityTo}
                      addEntityLabel={addEntityLabel}
                      LinkComponent={LinkComponent}
                      testidPrefix={testidPrefix}
                    />
                  </td>
                </tr>
              ) : (
                data.map((row, i) => {
                  const presentation = rowPresentation?.(row);
                  return (
                    <DesktopRow<R>
                      key={String(row.id)}
                      row={row}
                      {...(ariaRowCount !== undefined
                        ? { ariaRowIndex: rowIndexBase + i + 2 }
                        : {})}
                      columns={visibleColumns}
                      selectionEnabled={Boolean(selectionEnabled)}
                      selected={selection.has(row.id)}
                      onToggleSelected={(checked) => toggleSelected(row.id, checked)}
                      rowActions={manifest.row_actions}
                      onRowAction={handleRowActionClick}
                      {...(onRowClick ? { onRowClick } : {})}
                      {...(presentation ? { presentation } : {})}
                      resolveTerm={resolveTerm}
                      usePermission={usePermission}
                      testidPrefix={testidPrefix}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {/* Mobile: stacked cards. */}
        {isMobile && (
          // The same id as the desktop <table>, so "the list is shown" reads
          // the same at every width (product_filing phone journey P1).
          <div data-testid={`${testidPrefix}-table`}>
          {loading ? (
            <div
              className="p-3 flex flex-col gap-2"
              data-testid={`${testidPrefix}-loading`}
              // UI-20 §B.4: the loading CONTAINER carries aria-busy. It had
              // the skeletons and the testid and not this, so a screen-reader
              // user was told nothing about the wait at all.
              aria-busy="true"
            >
              {Array.from({ length: pageSize ?? manifest.page_size ?? 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <EmptyStateCell
              hasFiltersOrSearch={hasActiveFiltersOrSearch}
              onClearFilters={clearFilters}
              addEntityTo={addEntityTo}
              addEntityLabel={addEntityLabel}
              LinkComponent={LinkComponent}
              testidPrefix={testidPrefix}
            />
          ) : (
            <ul className="flex flex-col">
              {data.map((row) => (
                <MobileRowCard<R>
                  key={String(row.id)}
                  row={row}
                  columns={visibleColumns}
                  selectionEnabled={Boolean(selectionEnabled)}
                  selected={selection.has(row.id)}
                  onToggleSelected={(checked) => toggleSelected(row.id, checked)}
                  rowActions={manifest.row_actions}
                  onRowAction={handleRowActionClick}
                  {...(onRowClick ? { onRowClick } : {})}
                  resolveTerm={resolveTerm}
                  usePermission={usePermission}
                  testidPrefix={testidPrefix}
                />
              ))}
            </ul>
          )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && data.length > 0 && (
        <Pagination
          testidPrefix={testidPrefix}
          mode={nextCursor !== undefined || onCursorNext ? "cursor" : "offset"}
          page={page}
          pageSize={pageSize}
          total={total}
          rowCount={data.length}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          nextCursor={nextCursor}
          onCursorNext={onCursorNext}
          onCursorPrev={onCursorPrev}
        />
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        pending={pendingConfirm}
        onConfirm={confirmPending}
        onCancel={() => setPendingConfirm(null)}
        resolveTerm={resolveTerm}
        testidPrefix={testidPrefix}
      />
    </div>
  );
}

// ===========================================================================
// Toolbar — search + filter chips/dropdowns
// ===========================================================================

interface ToolbarProps {
  testidPrefix: string;
  search: string;
  onSearchChange?: (next: string) => void;
  filters: FilterState;
  filterSpecs: readonly FilterSpec[] | undefined;
  onFilterChange: (id: string, value: string | undefined) => void;
  resolveTerm?: (key: string) => string;
}

function Toolbar({
  testidPrefix,
  search,
  onSearchChange,
  filters,
  filterSpecs,
  onFilterChange,
  resolveTerm,
}: ToolbarProps): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {onSearchChange && (
        <Input
          type="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="Search"
          data-testid={`${testidPrefix}-search`}
          className="max-w-sm"
        />
      )}
      {filterSpecs?.map((spec) => (
        <FilterControl
          key={spec.id}
          spec={spec}
          value={filters[spec.id]}
          onChange={(v) => onFilterChange(spec.id, v)}
          resolveTerm={resolveTerm}
          testidPrefix={testidPrefix}
        />
      ))}
    </div>
  );
}

interface FilterControlProps {
  spec: FilterSpec;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  resolveTerm?: (key: string) => string;
  testidPrefix: string;
}

function FilterControl({
  spec,
  value,
  onChange,
  resolveTerm,
  testidPrefix,
}: FilterControlProps): ReactElement {
  const label = resolveLabel(spec, resolveTerm);

  if (spec.display === "dropdown") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-[var(--color-text-soft)] font-semibold">
          {label}
        </span>
        <Select
          value={value ?? "__all__"}
          onValueChange={(v) => onChange(v === "__all__" ? undefined : v)}
        >
          {/* Named by its label (the visible span is not a <label>, so the
           * trigger had no accessible name), and 44 px tall on a phone. */}
          <SelectTrigger
            aria-label={label}
            className="h-11 md:h-9 min-w-[160px]"
            data-testid={`${testidPrefix}-filter-${spec.id}`}
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            {spec.options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // chip_group
  const counts = spec.option_counts;
  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-testid={`${testidPrefix}-filter-${spec.id}`}
      className="inline-flex items-center gap-1 flex-wrap"
    >
      <ChipFilterButton
        active={value === undefined}
        label="All"
        count={counts?.["all"]}
        onClick={() => onChange(undefined)}
        testid={`${testidPrefix}-filter-${spec.id}-option-all`}
      />
      {spec.options.map((opt) => (
        <ChipFilterButton
          key={opt}
          active={value === opt}
          label={prettyFilterLabel(opt)}
          count={counts?.[opt]}
          onClick={() => onChange(opt)}
          testid={`${testidPrefix}-filter-${spec.id}-option-${opt}`}
        />
      ))}
    </div>
  );
}

/**
 * Convert a snake_case enum option (e.g. `on_hold`) to a sentence-case
 * UI label (`On hold`). Per Growth Fusion 2026-05-23 spec: never let
 * snake_case leak into the UI.
 */
function prettyFilterLabel(opt: string): string {
  if (!opt) return "";
  const cleaned = opt.replace(/_/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

interface ChipFilterButtonProps {
  active: boolean;
  label: string;
  /** When provided, renders as "Label (N)" and is included in aria-label. */
  count?: number;
  onClick: () => void;
  testid: string;
}

function ChipFilterButton({
  active,
  label,
  count,
  onClick,
  testid,
}: ChipFilterButtonProps): ReactElement {
  const ariaLabel =
    count !== undefined ? `${label}, ${count} records` : label;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={ariaLabel}
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1 rounded-[var(--radius-full)] border px-3 py-1 text-[12px] font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
        active
          ? "bg-[var(--color-brand-soft)] border-transparent text-[var(--color-brand-strong)]"
          : "bg-[var(--color-surface)] border-[var(--color-border-hairline)] text-[var(--color-text-soft)] hover:text-[var(--color-text-strong)] hover:bg-[var(--color-surface-soft)]",
      )}
    >
      {label}
      {count !== undefined && (
        <span
          className="text-[var(--color-text-faint)] font-normal"
          aria-hidden="true"
        >
          ({count})
        </span>
      )}
    </button>
  );
}

// ===========================================================================
// Sort icon — three-state
// ===========================================================================

function SortIcon({ direction }: { direction: "asc" | "desc" | null }): ReactElement {
  if (direction === "asc") return <ChevronUp size={12} aria-hidden="true" />;
  if (direction === "desc") return <ChevronDown size={12} aria-hidden="true" />;
  return <ChevronsUpDown size={12} aria-hidden="true" className="opacity-60" />;
}

// ===========================================================================
// Desktop row
// ===========================================================================

interface DesktopRowProps<R extends RowData> {
  row: R;
  columns: readonly ColumnSpec[];
  selectionEnabled: boolean;
  selected: boolean;
  onToggleSelected: (checked: boolean) => void;
  rowActions: readonly RowActionSpec[] | undefined;
  onRowAction: (action: RowActionSpec, row: R) => void;
  onRowClick?: (row: R) => void;
  /** Register descriptor from the host's `rowPresentation` — see the type. */
  presentation?: RowPresentation;
  resolveTerm?: (key: string) => string;
  usePermission: PermissionCheck;
  testidPrefix: string;
  /** The row's position in the whole table (header row = 1); see aria-rowcount. */
  ariaRowIndex?: number;
}

function DesktopRow<R extends RowData>({
  row,
  ariaRowIndex,
  columns,
  selectionEnabled,
  selected,
  onToggleSelected,
  rowActions,
  onRowAction,
  onRowClick,
  presentation,
  resolveTerm,
  usePermission,
  testidPrefix,
}: DesktopRowProps<R>): ReactElement {
  // Whole-row click navigates (when onRowClick is set). The handler lives on
  // the <tr> and ignores clicks that landed on an interactive child — the
  // name-cell <a> link, the row ⋯ menu, the selection checkbox — so those
  // keep their own behaviour. Keyboard activation, middle-click (open in new
  // tab) and the accessible name all come from the REAL <a> link in the name
  // cell, so the row itself carries NO role/tabIndex. (The previous approach
  // stamped role="button" + tabIndex on every cell, which nested the name
  // link / chevron inside an interactive cell — an axe "nested interactive"
  // violation. This avoids that while still making the whole row clickable.)
  const isClickable = Boolean(onRowClick);
  // Register machinery. `hideSelection` / `hideActions` keep the <td> (so the
  // column grid + aria-colcount hold) but render it EMPTY.
  const hideSelection = presentation?.hideSelection === true;
  const hideActions = presentation?.hideActions === true;
  return (
    <tr
      aria-rowindex={ariaRowIndex}
      aria-selected={
        selectionEnabled && !hideSelection ? selected : undefined
      }
      data-row-kind={presentation?.kind}
      data-testid={`${testidPrefix}-row-${row.id}`}
      onClick={
        isClickable
          ? (e) => {
              if (
                (e.target as HTMLElement).closest(
                  "a, button, input, label, [role='button'], [role='menu'], [role='menuitem']",
                )
              ) {
                return;
              }
              onRowClick?.(row);
            }
          : undefined
      }
      className={cn(
        "border-b border-[oklch(0%_0_0_/_0.06)] last:border-b-0",
        "hover:bg-[oklch(98%_0.005_240)] transition-colors",
        selected && "bg-[oklch(96%_0.02_250)]",
        isClickable && "cursor-pointer",
      )}
    >
      {selectionEnabled && (
        <td className="px-3 py-3 align-middle">
          {hideSelection ? null : (
            <input
              type="checkbox"
              aria-label={selectionLabel(row, columns)}
              checked={selected}
              onChange={(e) => onToggleSelected(e.target.checked)}
              data-testid={`${testidPrefix}-row-${row.id}-select`}
              className="h-4 w-4 cursor-pointer"
            />
          )}
        </td>
      )}
      {columns.map((col) => {
        const raw = lookupSource(row, col.source);
        const display = col.render
          ? col.render(row as unknown as Record<string, unknown>)
          : formatCell(raw, col.format);
        return (
          <td
            key={col.id}
            data-testid={`${testidPrefix}-row-${row.id}-cell-${col.id}`}
            // Column hiding is driven by real CSS keyed on data-priority (see
            // tokens.css), NOT by Tailwind responsive classes. Those were
            // never generated in this build — the stylesheet contained no
            // responsive utility at all — so `hidden` applied at every width
            // and every priority-2/3 column vanished permanently.
            data-priority={col.priority ?? 1}
            className={cn(
              "px-3 py-3 align-middle",
              col.align === "right" && "text-right tabular-nums",
              col.align === "center" && "text-center",
            )}
          >
            {display}
          </td>
        );
      })}
      {rowActions && rowActions.length > 0 && (
        <td className="px-3 py-3 align-middle text-right">
          {hideActions ? null : (
            <RowActionMenu<R>
              actions={rowActions}
              row={row}
              onAction={onRowAction}
              resolveTerm={resolveTerm}
              usePermission={usePermission}
              testidPrefix={testidPrefix}
            />
          )}
        </td>
      )}
    </tr>
  );
}

// ===========================================================================
// Mobile row card
// ===========================================================================

function MobileRowCard<R extends RowData>({
  row,
  columns,
  selectionEnabled,
  selected,
  onToggleSelected,
  rowActions,
  onRowAction,
  onRowClick,
  resolveTerm,
  usePermission,
  testidPrefix,
}: DesktopRowProps<R>): ReactElement {
  // The headline is whichever visible column has the lowest priority value
  // (1 = most important). Fall back to the first column.
  const headlineCol =
    [...columns].sort(
      (a, b) => (a.priority ?? 1) - (b.priority ?? 1),
    )[0] ?? columns[0];
  const bodyCols = columns.filter((c) => c.id !== headlineCol?.id);
  const headlineDisplay = headlineCol
    ? formatCell(lookupSource(row, headlineCol.source), headlineCol.format)
    : "";

  const isClickable = Boolean(onRowClick);
  return (
    <li
      // No aria-selected: it is not allowed on a list item (axe
      // aria-allowed-attr, critical). The row's checkbox carries the state.
      data-testid={`${testidPrefix}-row-${row.id}`}
      onClick={isClickable ? () => onRowClick?.(row) : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick?.(row);
              }
            }
          : undefined
      }
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      className={cn(
        "flex flex-col gap-2 px-3 py-3 border-b border-[oklch(0%_0_0_/_0.06)] last:border-b-0",
        selected && "bg-[oklch(96%_0.02_250)]",
        isClickable &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-inset",
      )}
    >
      <div className="flex items-start gap-3">
        {selectionEnabled && (
          <input
            type="checkbox"
            aria-label={selectionLabel(row, columns)}
            checked={selected}
            onChange={(e) => onToggleSelected(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testidPrefix}-row-${row.id}-select`}
            className="h-4 w-4 mt-1 cursor-pointer flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[var(--color-text-strong)] truncate">
            {headlineDisplay}
          </div>
          <dl className="mt-1 flex flex-col gap-0.5 text-sm">
            {bodyCols.map((col) => {
              const raw = lookupSource(row, col.source);
              const display = formatCell(raw, col.format);
              if (!display) return null;
              return (
                <div key={col.id} className="flex gap-2">
                  <dt className="text-[var(--color-text-soft)]">
                    {resolveLabel(col, resolveTerm)}:
                  </dt>
                  <dd className="text-[var(--color-text-strong)] truncate">
                    {display}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
        {rowActions && rowActions.length > 0 && (
          <div onClick={(e) => e.stopPropagation()}>
            <RowActionMenu<R>
              actions={rowActions}
              row={row}
              onAction={onRowAction}
              resolveTerm={resolveTerm}
              usePermission={usePermission}
              testidPrefix={testidPrefix}
            />
          </div>
        )}
      </div>
    </li>
  );
}

// ===========================================================================
// Row action menu (DropdownMenu wrapper)
// ===========================================================================

interface RowActionMenuProps<R extends RowData> {
  actions: readonly RowActionSpec[];
  row: R;
  onAction: (action: RowActionSpec, row: R) => void;
  resolveTerm?: (key: string) => string;
  usePermission: PermissionCheck;
  testidPrefix: string;
}

function RowActionMenu<R extends RowData>({
  actions,
  row,
  onAction,
  resolveTerm,
  usePermission,
  testidPrefix,
}: RowActionMenuProps<R>): ReactElement | null {
  const visible = actions.filter(
    (a) =>
      hasAllPermissions(a.requires_permissions, usePermission) &&
      !(a.hidden?.(row as unknown as Record<string, unknown>) ?? false),
  );
  if (visible.length === 0) return null;

  return (
    <RowActionMenuControlled<R>
      visible={visible}
      row={row}
      onAction={onAction}
      resolveTerm={resolveTerm}
      testidPrefix={testidPrefix}
    />
  );
}

/**
 * The menu itself. Radix opens a dropdown on pointer-down and on Enter/Space;
 * a plain `click` (a test's fireEvent.click, an assistive tool's synthetic
 * activation) never opened it. It now also opens on click when closed. A real
 * mouse click on the trigger of an OPEN menu first closes it on pointer-down
 * (outside the menu), so a click within a moment of closing is ignored rather
 * than reopening it.
 */
function RowActionMenuControlled<R extends RowData>({
  visible,
  row,
  onAction,
  resolveTerm,
  testidPrefix,
}: {
  visible: readonly RowActionSpec[];
  row: R;
  onAction: (action: RowActionSpec, row: R) => void;
  resolveTerm?: (key: string) => string;
  testidPrefix: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const closedAt = useRef(0);
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (!next) closedAt.current = Date.now();
        setOpen(next);
      }}
    >
      <DropdownMenuTrigger
        aria-label="Row actions"
        onClick={() => {
          if (!open && Date.now() - closedAt.current > 300) setOpen(true);
        }}
        data-testid={`${testidPrefix}-row-${row.id}-actions`}
        className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-soft)] hover:bg-[oklch(0%_0_0_/_0.04)] hover:text-[var(--color-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
      >
        <Ellipsis size={16} aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {visible.map((action) => {
          const Icon = action.icon ? ROW_ACTION_ICONS[action.icon] : null;
          const label = resolveLabel(action, resolveTerm);
          return (
            <DropdownMenuItem
              key={action.id}
              onSelect={() => onAction(action, row)}
              data-testid={`${testidPrefix}-row-${row.id}-action-${action.id}`}
              className={cn(
                action.confirm?.destructive &&
                  "text-[var(--color-accent-red)] data-[highlighted]:text-[var(--color-accent-red)]",
              )}
            >
              {Icon && <Icon size={14} aria-hidden="true" />}
              <span>{label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ===========================================================================
// Bulk-action toolbar
// ===========================================================================

interface BulkActionToolbarProps {
  testidPrefix: string;
  selectedCount: number;
  bulkActions: readonly BulkActionSpec[];
  onAction: (action: BulkActionSpec) => void;
  onClear: () => void;
  resolveTerm?: (key: string) => string;
  usePermission: PermissionCheck;
}

function BulkActionToolbar({
  testidPrefix,
  selectedCount,
  bulkActions,
  onAction,
  onClear,
  resolveTerm,
  usePermission,
}: BulkActionToolbarProps): ReactElement {
  const visible = bulkActions.filter((a) =>
    hasAllPermissions(a.requires_permissions, usePermission),
  );

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      data-testid={`${testidPrefix}-bulk-toolbar`}
      className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-[var(--radius-md)] bg-[oklch(95%_0.04_250)] border border-[oklch(78%_0.08_250)]"
    >
      <span className="text-sm font-semibold text-[oklch(28%_0.13_250)]">
        {selectedCount} selected
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {visible.map((action) => (
          <Button
            key={action.id}
            size="sm"
            variant="glass-strong"
            onClick={() => onAction(action)}
            data-testid={`${testidPrefix}-bulk-action-${action.id}`}
          >
            {resolveLabel(action, resolveTerm)}
          </Button>
        ))}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onClear}
        aria-label="Clear selection"
        data-testid={`${testidPrefix}-bulk-clear`}
        className="ml-auto"
      >
        <X size={14} aria-hidden="true" />
        Clear
      </Button>
    </div>
  );
}

// ===========================================================================
// Confirm dialog (single instance — multiplexed across row + bulk actions)
// ===========================================================================

interface ConfirmDialogProps<R extends RowData> {
  pending:
    | { kind: "row"; action: RowActionSpec; row: R }
    | { kind: "bulk"; action: BulkActionSpec }
    | null;
  onConfirm: () => void;
  onCancel: () => void;
  resolveTerm?: (key: string) => string;
  testidPrefix: string;
}

function ConfirmDialog<R extends RowData>({
  pending,
  onConfirm,
  onCancel,
  resolveTerm,
  testidPrefix,
}: ConfirmDialogProps<R>): ReactElement {
  const open = pending !== null;
  const confirm = pending?.action.confirm;
  const title = confirm?.title ?? "Are you sure?";
  const body = confirm?.body ?? "This action cannot be undone.";
  const destructive =
    pending?.kind === "row" && pending.action.confirm?.destructive === true;
  const actionLabel = pending ? resolveLabel(pending.action, resolveTerm) : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent data-testid={`${testidPrefix}-confirm`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            data-testid={`${testidPrefix}-confirm-cancel`}
          >
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "primary"}
            onClick={onConfirm}
            data-testid={`${testidPrefix}-confirm-ok`}
          >
            {actionLabel || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Loading rows
// ===========================================================================

function LoadingRows({
  columnCount,
  rowCount,
  testidPrefix,
}: {
  columnCount: number;
  rowCount: number;
  testidPrefix: string;
}): ReactElement {
  return (
    <Fragment>
      {Array.from({ length: rowCount }).map((_, i) => (
        <tr
          key={i}
          data-testid={i === 0 ? `${testidPrefix}-loading` : undefined}
          // UI-20 §B.4 — see the mobile branch above. The first skeleton row
          // is what carries the table's loading testid, so it is also what has
          // to carry the busy state.
          {...(i === 0 ? { "aria-busy": "true" as const } : {})}
        >
          {Array.from({ length: columnCount }).map((__, j) => (
            <td key={j} className="px-3 py-3">
              <Skeleton className="h-4 w-full max-w-[160px]" />
            </td>
          ))}
        </tr>
      ))}
    </Fragment>
  );
}

// ===========================================================================
// Empty state
// ===========================================================================

interface EmptyStateCellProps {
  hasFiltersOrSearch: boolean;
  onClearFilters: () => void;
  addEntityTo?: string | undefined;
  addEntityLabel?: string | undefined;
  LinkComponent: LinkRenderer;
  testidPrefix: string;
}

function EmptyStateCell({
  hasFiltersOrSearch,
  onClearFilters,
  addEntityTo,
  addEntityLabel,
  LinkComponent,
  testidPrefix,
}: EmptyStateCellProps): ReactElement {
  if (hasFiltersOrSearch) {
    return (
      <div
        role="status"
        data-testid={`${testidPrefix}-empty-filtered`}
        className="grid place-items-center min-h-40 p-6 text-center gap-3"
      >
        <div className="text-sm text-[var(--color-text-soft)]">
          No results match the current filters.
        </div>
        <Button
          variant="glass-strong"
          size="sm"
          onClick={onClearFilters}
          data-testid={`${testidPrefix}-empty-filtered-clear`}
        >
          Clear filters
        </Button>
      </div>
    );
  }
  return (
    <div
      role="status"
      data-testid={`${testidPrefix}-empty`}
      className="grid place-items-center min-h-40 p-6 text-center gap-3"
    >
      <div className="text-sm text-[var(--color-text-soft)]">
        Nothing here yet.
      </div>
      {addEntityTo && (
        <LinkComponent
          to={addEntityTo}
          data-testid={`${testidPrefix}-empty-add`}
          className="inline-flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-[var(--color-text-on-dark)] border border-[var(--color-brand-strong)] h-9 px-4 text-sm font-medium hover:bg-[var(--color-brand-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
        >
          + {addEntityLabel ?? "Add"}
        </LinkComponent>
      )}
    </div>
  );
}

// ===========================================================================
// Error panel
// ===========================================================================

interface ErrorPanelProps {
  message: string;
  onRetry?: () => void;
  testid: string;
}

function ErrorPanel({ message, onRetry, testid }: ErrorPanelProps): ReactElement {
  return (
    <div
      role="alert"
      data-testid={testid}
      className="grid place-items-center min-h-40 p-6 text-center gap-3 rounded-[var(--radius-md)] border border-[oklch(78%_0.10_25)] bg-[oklch(95%_0.04_25)]"
    >
      <div className="text-sm text-[oklch(30%_0.16_25)]">
        {message || "Something went wrong."}
      </div>
      {onRetry && (
        <Button
          variant="glass-strong"
          size="sm"
          onClick={onRetry}
          data-testid={`${testid}-retry`}
        >
          Retry
        </Button>
      )}
    </div>
  );
}

// ===========================================================================
// Pagination — offset OR cursor
// ===========================================================================

interface PaginationProps {
  testidPrefix: string;
  mode: "offset" | "cursor";
  page?: number;
  pageSize?: number;
  total?: number;
  rowCount: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  nextCursor?: string | null;
  onCursorNext?: () => void;
  onCursorPrev?: () => void;
}

function Pagination(props: PaginationProps): ReactElement | null {
  const {
    testidPrefix,
    mode,
    page,
    pageSize,
    total,
    rowCount,
    onPageChange,
    onPageSizeChange,
    nextCursor,
    onCursorNext,
    onCursorPrev,
  } = props;

  // Hide entirely if no handlers are wired (host opted out of pagination).
  if (mode === "offset" && !onPageChange && !onPageSizeChange) return null;
  if (mode === "cursor" && !onCursorNext) return null;

  if (mode === "cursor") {
    const hasNext = nextCursor !== null && nextCursor !== undefined;
    return (
      <div
        className="flex items-center justify-between gap-3"
        data-testid={`${testidPrefix}-pagination`}
      >
        <span className="text-sm text-[var(--color-text-soft)]">
          Showing {rowCount} {rowCount === 1 ? "result" : "results"}
        </span>
        <div className="flex items-center gap-2">
          {onCursorPrev && (
            <Button
              size="sm"
              variant="glass-strong"
              onClick={onCursorPrev}
              data-testid={`${testidPrefix}-pagination-prev`}
            >
              <ChevronLeft size={14} aria-hidden="true" />
              Previous
            </Button>
          )}
          <Button
            size="sm"
            variant="glass-strong"
            disabled={!hasNext}
            onClick={() => onCursorNext?.()}
            data-testid={`${testidPrefix}-pagination-next`}
          >
            Next
            <ChevronRight size={14} aria-hidden="true" />
          </Button>
        </div>
      </div>
    );
  }

  // offset mode
  const currentPage = page ?? 1;
  const currentSize = pageSize ?? 25;
  const start = total === 0 ? 0 : (currentPage - 1) * currentSize + 1;
  const end = total === undefined
    ? (currentPage - 1) * currentSize + rowCount
    : Math.min(total, (currentPage - 1) * currentSize + rowCount);
  const lastPage = total !== undefined
    ? Math.max(1, Math.ceil(total / currentSize))
    : undefined;
  const canPrev = currentPage > 1;
  const canNext =
    lastPage !== undefined ? currentPage < lastPage : rowCount === currentSize;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3"
      data-testid={`${testidPrefix}-pagination`}
    >
      <div className="flex items-center gap-2 text-sm text-[var(--color-text-soft)]">
        {total !== undefined ? (
          <span>
            Showing <strong className="text-[var(--color-text-strong)]">{start}</strong>
            –<strong className="text-[var(--color-text-strong)]">{end}</strong> of{" "}
            <strong className="text-[var(--color-text-strong)]">{total}</strong>
          </span>
        ) : (
          <span>Showing {rowCount}</span>
        )}
        {onPageSizeChange && (
          <div className="flex items-center gap-2 ml-3">
            <span className="text-xs uppercase tracking-wide font-semibold">
              Per page
            </span>
            <Select
              value={String(currentSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger
                aria-label="Rows per page"
                className="h-11 md:h-8 w-[80px]"
                data-testid={`${testidPrefix}-pagination-size`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="glass-strong"
          disabled={!canPrev}
          onClick={() => onPageChange?.(currentPage - 1)}
          data-testid={`${testidPrefix}-pagination-prev`}
        >
          <ChevronLeft size={14} aria-hidden="true" />
          Previous
        </Button>
        <Button
          size="sm"
          variant="glass-strong"
          disabled={!canNext}
          onClick={() => onPageChange?.(currentPage + 1)}
          data-testid={`${testidPrefix}-pagination-next`}
        >
          Next
          <ChevronRight size={14} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

// ===========================================================================
// Re-exports of helpers (the only public child for now is KenDataTable;
// internal helpers are kept private to preserve a small surface).
// ===========================================================================

export { type ReactNode };
